import { Component, OnInit, OnDestroy, ChangeDetectorRef, Input } from '@angular/core';
import { HostListener } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { RegistrationService } from '../registration.service';
import { HttpService } from '@core/services/http.service';
import { LocalService } from '@app/@core/services/local.service';
import { forkJoin, of, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs';
import { API_ENDPOINTS } from '@app/shared/constants/apiconstants';

@Component({
  standalone: false,
  selector: 'app-phone-recovery-email',
  templateUrl: './phone-recovery-email.component.html',
  styleUrls: ['./phone-recovery-email.component.scss']
})
export class PhoneRecoveryEmailComponent implements OnInit, OnDestroy {
  // Listen for browser back/forward navigation
  @HostListener('window:popstate', ['$event'])
  onPopState(event: any) {
    // If recovery email is verified, redirect to the correct screen
    if (localStorage.getItem('recoveryEmailVerified') === 'true') {
      // Replace with the correct route for the verified state
      this.router.navigate(['/registration/recovery-email-verified'], {
        queryParams: {
          email: this.email,
          recoveryEmail: this.recoveryEmail,
          phone: this.phone
        }
      });
    }
  }

  // Call this method when recovery email is successfully verified
  setRecoveryEmailVerifiedFlag() {
    localStorage.setItem('recoveryEmailVerified', 'true');
  }
  @Input() formData: any;
  // Form data
  email: string = '';
  recoveryEmail: string = '';
  phone: string = '';
  phoneNumber: string = '';
  
  // Recovery Email OTP functionality
  otpLength: number = 6;
  recoveryOtpDigits: string[] = [];
  recoveryResendTimer: number = 30; // 30 seconds timer for resend
  recoveryTimeRemaining: number = 0; // Start with 0 so button is enabled initially
  recoveryFormattedTime: string = '00:00';
  private recoveryTimerInterval: any;
  
  // Recovery Email OTP validation and attempts
  private readonly correctRecoveryOtp: string = '628170';
  recoveryAttempts: number = 0;
  maxAttempts: number = 4; // Default fallback - will be updated from API
  recoveryResendDisabledUntil: number | null = null; // timestamp until which resend is disabled
  recoveryResendCount: number = 0;
  maxResendCount: number = 5; // Default fallback - will be updated from API
  otpLockoutDuration: number = 3; // Default 3 minutes - will be updated from API
  otpResendLockoutDuration: number = 15; // Default 15 minutes - hardcoded lockout after max attempts
  otpResendCooldown: number = 1; // Default 1 minute - will be updated from API
  isBlocked: boolean = false;
  showRecoveryOtpError: boolean = false;
  recoveryEmptyFieldsAfterBlur: boolean[] = new Array(6).fill(false);
  recoveryHadFocus: boolean = false;

  // Phone OTP functionality  
  phoneOtpDigits: string[] = [];
  showPhoneOtpError: boolean = false;
  phoneEmptyFieldsAfterBlur: boolean[] = new Array(6).fill(false);
  phoneHadFocus: boolean = false;
  phoneResendTimer: number = 30; // 30 seconds timer for resend
  phoneTimeRemaining: number = 0; // Start with 0 so button is enabled initially
  phoneFormattedTime: string = '00:00';
  private phoneTimerInterval: any;
  
  // Phone OTP validation and attempts
  private readonly correctPhoneOtp: string = '628170';
  phoneAttempts: number = 0;
  phoneResendDisabledUntil: number | null = null; // timestamp until which resend is disabled
  phoneResendCount: number = 0;
  isPhoneBlocked: boolean = false;

  // Toast message state
  showToast: boolean = false;
  toastMessage: string = '';
  toastTitle: string = '';
  toastType: 'success' | 'error' = 'success';
  private toastTimer: any;

  // Email verification state
  otpSent: boolean = false;
  isEmailVerified: boolean = false;
  recoveryEmailVerified: boolean = false; // persistent flag
  
  // Phone verification state
  phoneOtpSent: boolean = false;
  isPhoneVerified: boolean = false;
  // Error handling
  showErrorMessage: boolean = false;
  errorMessage: string = '';
  genericMessages: any[] = [];
  genericMessage: any = {};
  errorMessages: any[] = [];
  validationerrorMessage: any = {};
  toastMessages: any[] = [];
  toastDbMessage: any = {};
  registeredData: any = '';
  // Modal state
  showConfirmModal: boolean = false;
  showContent: boolean = false;
  date: number = 0;
  private destroy$ = new Subject<void>();
  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly RegistrationService: RegistrationService,
    private readonly httpService: HttpService,
    private readonly sessionStorage: LocalService
  ) {
    // Update formatted time display
    this.updateRecoveryFormattedTime();
    this.updatePhoneFormattedTime();
  }
  getRegistrationDetails() {
      this.httpService.get(`${API_ENDPOINTS.GET_DETAILS}${this.email}`)
        .pipe(take(1),takeUntil(this.destroy$))
        .subscribe({
        next: (response: any) => {
          this.registeredData = response?.data?.data;
          if(this.registeredData?.cd_registration_status && this.registeredData.cd_registration_status === '4'){
            this.router.navigate(['/reg-form/reg-expired'], { queryParams: { email: this.email,cd_status:'completed' } });
          }else {
          this.showContent = true; // Show content if not expired
        }
        }
      });
  }

  ngOnInit() {
    this.showContent = false;
    this.loadOtpValidationRules();
    // Check persistent flag for recovery email verification
    const recoveryEmailVerifiedFlag = localStorage.getItem('recoveryEmailVerified');
    if (recoveryEmailVerifiedFlag === 'true') {
      this.recoveryEmailVerified = true;
      this.isEmailVerified = true; // Optionally set this if you want to skip UI
    }

    this.route.queryParams.subscribe(params => {
      if (params['email']) {
        this.email = decodeURIComponent(params['email']);
      } else {
        this.email = '';
        this.showContent = false;
      }
      if (params['date']) {
        const dateParam = decodeURIComponent(params['date']);
        // Parse the date parameter and convert to milliseconds
        const urlDate = new Date(dateParam).getTime();
        const urlTimeZone = new Date(dateParam).getTimezoneOffset();
        const currentDate = new Date().getTime();
        const currentTimeZone = new Date().getTimezoneOffset();
        if (urlTimeZone !== currentTimeZone) {
          this.showContent = false;
          this.router.navigate(['/reg-form/reg-expired'], { queryParams: { email: this.email } });
          return;
        }
        // Calculate difference in milliseconds
        const timeDifference = currentDate - urlDate;
        if (timeDifference > 86400000) {
          this.showContent = false;
          this.router.navigate(['/reg-form/reg-expired'], { queryParams: { email: this.email } });
          return;
        }
      }

      if (params['recoveryEmail']) {
        this.recoveryEmail = decodeURIComponent(params['recoveryEmail']);
      } else {
        this.recoveryEmail = '';
      }

      if (params['phone']) {
        this.phone = decodeURIComponent(params['phone']);
        this.phoneNumber = this.phone;
        // Remove any non-digit characters to get clean phone number
        const cleanedPhone = this.phoneNumber.replace(/\D/g, '');
        if (cleanedPhone.length === 10) {
          this.phoneNumber = `(${cleanedPhone.substring(0, 3)}) ${cleanedPhone.substring(3, 6)}-${cleanedPhone.substring(6)}`;
        } else if (cleanedPhone.length === 11) {
          this.phoneNumber = `+1 (${cleanedPhone.substring(1, 4)}) ${cleanedPhone.substring(4, 7)}-${cleanedPhone.substring(7)}`;
        } else {
          this.phoneNumber = cleanedPhone;
        }
      } else {
        this.phone = '';
        this.phoneNumber = '';
      }

      if (params['recoveryEmailVerified'] === 'true') {
        this.recoveryEmailVerified = true;
        this.isEmailVerified = true;
        localStorage.setItem('recoveryEmailVerified', 'true');
      }

      // Now, check registration status before loading the rest of the page
      this.httpService.get(`${API_ENDPOINTS.GET_DETAILS}${this.email}`)
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe({
          next: (response: any) => {
            this.registeredData = response?.data?.data;
            if (this.registeredData?.cd_registration_status && this.registeredData.cd_registration_status === '4') {
              this.router.navigate(['/reg-form/reg-expired'], { queryParams: { email: this.email, cd_status: 'completed' } });
              return;
            }
            this.showContent = true;
            // Only load the rest of the page if status is not 4
            this.clearPhoneTimer();
            // --- FIX: Always reset phone OTP state when returning to this screen ---
            const phoneTimerState = JSON.parse(this.sessionStorage.getItem(`phoneTimerState_${this.phone}`) || '{}');
            if (phoneTimerState && phoneTimerState.lockoutUntil && Date.now() < phoneTimerState.lockoutUntil) {
              this.phoneResendDisabledUntil = phoneTimerState.lockoutUntil;
              this.phoneTimeRemaining = Math.ceil((phoneTimerState.lockoutUntil - Date.now()) / 1000);
              this.updatePhoneFormattedTime();
              this.startPhoneTimer();
              this.isPhoneBlocked = true;
              this.phoneResendCount = phoneTimerState.resendCount || 0;
            } else if (phoneTimerState && phoneTimerState.timeRemaining > 0) {
              this.phoneTimeRemaining = phoneTimerState.timeRemaining;
              this.updatePhoneFormattedTime();
              this.startPhoneTimer();
              this.isPhoneBlocked = false;
              this.phoneResendCount = phoneTimerState.resendCount || 0;
            } else {
              this.isPhoneBlocked = false;
              this.phoneTimeRemaining = 0;
              this.phoneResendDisabledUntil = null;
              this.phoneResendCount = 0;
              this.sessionStorage.removeItem(`phoneTimerState_${this.phone}`);
            }
            // --- END FIX ---
            this.loadData();
            this.initializeRecoveryOtp();
            // Auto-send OTP without showing toast or starting timer
            this.otpSent = true;
            let input = {
              email: this.email,
              registrationStatus: 3
            }
            this.RegistrationService.updateRegistrationStatus(input).subscribe(
              (response) => {
                // Registration status updated
              },
              (error) => {
                // Error updating registration status
              }
            );
            // Recovery resend lockout logic (persist timer state)
            const recoveryTimerState = JSON.parse(localStorage.getItem(`recoveryTimerState_${this.recoveryEmail}`) || '{}');
            if (recoveryTimerState && recoveryTimerState.lockoutUntil && Date.now() < recoveryTimerState.lockoutUntil) {
              this.recoveryResendDisabledUntil = recoveryTimerState.lockoutUntil;
              this.recoveryTimeRemaining = Math.ceil((recoveryTimerState.lockoutUntil - Date.now()) / 1000);
              this.updateRecoveryFormattedTime();
              this.startRecoveryTimer();
              this.isBlocked = true;
              this.showErrorMessage = true;
              this.errorMessage = 'Resend Code is disabled due to too many failed attempts. Please try again after 15 minutes.';
            } else if (recoveryTimerState && recoveryTimerState.timeRemaining > 0) {
              this.recoveryTimeRemaining = recoveryTimerState.timeRemaining;
              this.updateRecoveryFormattedTime();
              this.startRecoveryTimer();
              this.isBlocked = false;
            } else {
              // fallback to legacy logic if timer state not found
              const lockoutUntil = Number(localStorage.getItem(`recoveryResendDisabledUntil_${this.recoveryEmail}`) || '0');
              if (lockoutUntil && Date.now() < lockoutUntil) {
                this.recoveryResendDisabledUntil = lockoutUntil;
                this.recoveryTimeRemaining = Math.ceil((lockoutUntil - Date.now()) / 1000);
                this.updateRecoveryFormattedTime();
                this.startRecoveryTimer();
                this.isBlocked = true;
                this.showErrorMessage = true;
                this.errorMessage = 'Resend Code is disabled due to too many failed attempts. Please try again after 15 minutes.';
              } else {
                this.recoveryResendDisabledUntil = null;
                this.isBlocked = false;
                localStorage.removeItem(`recoveryResendDisabledUntil_${this.recoveryEmail}`);
                localStorage.removeItem(`recoveryTimerState_${this.recoveryEmail}`);
              }
            }
            const lastRecoveryEmail = localStorage.getItem('lastRecoveryEmail');
            if (lastRecoveryEmail !== this.recoveryEmail) {
              localStorage.removeItem('recoveryResendDisabledUntil');
              localStorage.setItem('lastRecoveryEmail', this.recoveryEmail);
            }
            // Don't start phone timer on initial load as requested
          },
          error: (error) => {
            // If error in fetching registration details, optionally handle here
            this.router.navigate(['/reg-form/reg-expired'], { queryParams: { email: this.email } });
          }
        });
    });
  }

  private loadOtpValidationRules() {
    // Fetch maximum OTP attempts
    this.RegistrationService.getBusinessRuleByKey('Security', 'otp_verification_attempt_limit')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.data?.data?.intValue) {
            this.maxAttempts = response.data.data.intValue;
          }
        },
        error: (error) => {
          console.error('Error fetching maximum OTP attempts rule:', error);
        }
      });

    // Fetch maximum OTP resend count
    this.RegistrationService.getBusinessRuleByKey('Security', 'otp_resend_attempt_limit')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.data?.data?.intValue) {
            this.maxResendCount = response.data.data.intValue;
          }
        },
        error: (error) => {
          console.error('Error fetching maximum OTP resend count rule:', error);
        }
      });

    // Fetch OTP lockout duration
    this.RegistrationService.getBusinessRuleByKey('Security', 'verify_button_lock_duration')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.data?.data?.intValue) {
            this.otpLockoutDuration = response.data.data.intValue;
          }
        },
        error: (error) => {
          console.error('Error fetching OTP lockout duration rule:', error);
        }
      });

    // Fetch OTP resend short disable duration (for normal resend button disable)
    this.RegistrationService.getBusinessRuleByKey('Security', 'resend_code_short_disable_duration')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.data?.data?.intValue) {
            this.otpResendCooldown = response.data.data.intValue;
            // Update both recovery and phone resend timers (value is already in seconds)
            this.recoveryResendTimer = this.otpResendCooldown;
            this.phoneResendTimer = this.otpResendCooldown;
          }
        },
        error: (error) => {
          console.error('Error fetching OTP resend short disable duration rule:', error);
        }
      });

    // Fetch OTP resend long lockout duration (after max attempts reached)
    this.RegistrationService.getBusinessRuleByKey('Security', 'resend_code_lock_duration')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.data?.data?.intValue) {
            this.otpResendLockoutDuration = response.data.data.intValue;
          }
        },
        error: (error) => {
          console.error('Error fetching OTP resend long lockout duration rule:', error);
        }
      });
  }

  loadData(): void {
    const errorMessagesData = localStorage.getItem('errorMessages');
    const genericMessagesData = localStorage.getItem('genericMessages');
    const toastMessagesData = localStorage.getItem('toastMessages');
    if (!errorMessagesData || !genericMessagesData || !toastMessagesData) {
      this.loadDropdownData();
    } else {
      const storedErrorMessages = window.localStorage.getItem('errorMessages');
      this.errorMessages = JSON.parse(storedErrorMessages);

      this.validationerrorMessage = {};
      this.errorMessages.forEach(message => {
        if (message.key && message.value !== undefined) {
          this.validationerrorMessage[message.key] = message.value;
        }
      });
      const storedGenericMessages = window.localStorage.getItem('genericMessages');
      this.genericMessages = JSON.parse(storedGenericMessages);

      this.genericMessage = {};
      this.genericMessages.forEach(message => {
        if (message.key && message.value !== undefined) {
          this.genericMessage[message.key] = message.value;
        }
      });
      const storedToastMessages = window.localStorage.getItem('toastMessages');
      this.toastMessages = JSON.parse(storedToastMessages);

      this.toastDbMessage = {};
      this.toastMessages.forEach(message => {
        if (message.key && message.value !== undefined) {
          this.toastDbMessage[message.key] = message.value;
        }
      });
    }
  }
  private async loadDropdownData() {
    forkJoin([
      this.httpService.get('/domain/lookup?requestType=MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=TOAST MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=GENERIC MESSAGES')
    ]).subscribe({
      next: (item: any) => {
        this.errorMessages = item[0].data;
        this.toastMessages = item[1].data;
        this.genericMessages = item[2].data;
        // Process generic messages to make them easily accessible by key
        this.validationerrorMessage = {};
        this.errorMessages.forEach(message => {
          if (message.key && message.value !== undefined) {
            this.validationerrorMessage[message.key] = message.value;
          }
        });
        window.localStorage.setItem('errorMessages', JSON.stringify(this.errorMessages));
        this.toastDbMessage = {};
        this.toastMessages.forEach(message => {
          if (message.key && message.value !== undefined) {
            this.toastDbMessage[message.key] = message.value;
          }
        });
        window.localStorage.setItem('toastMessages', JSON.stringify(this.toastMessages));
        console.log(item);
        this.genericMessage = {};
        this.genericMessages.forEach(message => {
          if (message.key && message.value !== undefined) {
            this.genericMessage[message.key] = message.value;
          }
        });
        window.localStorage.setItem('genericMessages', JSON.stringify(this.genericMessages));
      },
      error: error => {
        console.error('Error loading dropdown data:', error);
      }
    });
  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    
    if (this.recoveryTimerInterval) {
      clearInterval(this.recoveryTimerInterval);
    }

    if (this.phoneTimerInterval) {
      clearInterval(this.phoneTimerInterval);
    }

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
  }



  initializeRecoveryOtp() {
    this.recoveryOtpDigits = new Array(this.otpLength).fill('');
    this.recoveryEmptyFieldsAfterBlur = new Array(this.otpLength).fill(false);
    this.showRecoveryOtpError = false;
    this.recoveryHadFocus = false;
    // Initialize timeRemaining to 0 so timer doesn't show initially
    this.recoveryTimeRemaining = 0;
    this.updateRecoveryFormattedTime();
  }

  initializePhoneOtp() {
    this.phoneOtpDigits = new Array(this.otpLength).fill('');
    this.phoneEmptyFieldsAfterBlur = new Array(this.otpLength).fill(false);
    this.showPhoneOtpError = false;
    this.phoneHadFocus = false;
    // Initialize timeRemaining to 0 so timer doesn't show initially
    this.phoneTimeRemaining = 0;
    this.updatePhoneFormattedTime();
  }

    closeErrorToast() {
    this.showRecoveryOtpError = false;
    this.showPhoneOtpError = false;
  }

  // Recovery Email OTP methods
  onRecoveryInputChange(event: any, index: number) {
    const value = event.target.value;

    // Only allow single digit numbers
    if (value.length > 1) {
      event.target.value = value.slice(-1);
    }

    if (!/^\d*$/.test(event.target.value)) {
      event.target.value = '';
      return;
    }

    this.recoveryOtpDigits[index] = event.target.value;
    
    // If there's a value, clear any error state for this field
    if (event.target.value) {
      this.recoveryEmptyFieldsAfterBlur[index] = false;
      
      // If all fields have values, clear the error message
      if (!this.recoveryOtpDigits.includes('')) {
        this.showRecoveryOtpError = false;
      }
    }

    // Auto-focus next input
    if (event.target.value && index < this.otpLength - 1) {
      const nextInput = event.target.nextElementSibling;
      if (nextInput) {
        nextInput.focus();
      }
    }

    this.checkRecoveryIfComplete();
  }

  onRecoveryKeyDown(event: any, index: number) {
    // Handle backspace
    if (event.key === 'Backspace' && !event.target.value && index > 0) {
      const prevInput = event.target.previousElementSibling;
      if (prevInput) {
        prevInput.focus();
        this.recoveryOtpDigits[index - 1] = '';
        prevInput.value = '';
      }
    }
  }

  onRecoveryPaste(event: any) {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').split('').slice(0, this.otpLength);

    for (let i = 0; i < this.otpLength; i++) {
      this.recoveryOtpDigits[i] = digits[i] || '';
      const input = document.getElementById(`recovery-otp-input-${i}`) as HTMLInputElement;
      if (input) {
        input.value = this.recoveryOtpDigits[i];
      }
    }

    this.checkRecoveryIfComplete();
  }

  checkRecoveryIfComplete() {
    const isComplete = this.recoveryOtpDigits.every(digit => digit !== '');
    if (isComplete) {
      const otpValue = this.recoveryOtpDigits.join('');
      console.log('Recovery OTP entered:', otpValue);
    }
  }

  // Phone OTP methods
  onPhoneInputChange(event: any, index: number) {
    const value = event.target.value;

    // Only allow single digit numbers
    if (value.length > 1) {
      event.target.value = value.slice(-1);
    }

    if (!/^\d*$/.test(event.target.value)) {
      event.target.value = '';
      return;
    }

    this.phoneOtpDigits[index] = event.target.value;
    
    // If there's a value, clear any error state for this field
    if (event.target.value) {
      this.phoneEmptyFieldsAfterBlur[index] = false;
      
      // If all fields have values, clear the error message
      if (!this.phoneOtpDigits.includes('')) {
        this.showPhoneOtpError = false;
      }
    }

    // Auto-focus next input
    if (event.target.value && index < this.otpLength - 1) {
      const nextInput = event.target.nextElementSibling;
      if (nextInput) {
        nextInput.focus();
      }
    }

    this.checkPhoneIfComplete();
  }

  onPhoneKeyDown(event: any, index: number) {
    // Handle backspace
    if (event.key === 'Backspace' && !event.target.value && index > 0) {
      const prevInput = event.target.previousElementSibling;
      if (prevInput) {
        prevInput.focus();
        this.phoneOtpDigits[index - 1] = '';
        prevInput.value = '';
      }
    }
  }

  onPhonePaste(event: any) {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').split('').slice(0, this.otpLength);

    for (let i = 0; i < this.otpLength; i++) {
      this.phoneOtpDigits[i] = digits[i] || '';
      const input = document.getElementById(`phone-otp-input-${i}`) as HTMLInputElement;
      if (input) {
        input.value = this.phoneOtpDigits[i];
      }
    }

    this.checkPhoneIfComplete();
  }

  checkPhoneIfComplete() {
    const isComplete = this.phoneOtpDigits.every(digit => digit !== '');
    if (isComplete) {
      const otpValue = this.phoneOtpDigits.join('');
      console.log('Phone OTP entered:', otpValue);
    }
  }

  // Recovery OTP blur and focus handlers
  onRecoveryInputBlur(event: any, index: number) {
    this.recoveryHadFocus = true;
    
    // If the field is empty after blur, mark it for error display
    if (!event.target.value) {
      this.recoveryEmptyFieldsAfterBlur[index] = true;
    }
    
    // Check if all fields are empty to show the error message
    const allEmpty = this.recoveryOtpDigits.every(digit => digit === '');
    
    // Show error if all fields are empty after having focus
    this.showRecoveryOtpError = allEmpty;
    
    // Update UI to mark which fields are empty
    this.updateRecoveryEmptyFieldsState();
  }
  
  // Helper to update empty fields state
  updateRecoveryEmptyFieldsState() {
    // Mark all empty fields with error state if we've had focus
    if (this.recoveryHadFocus) {
      for (let i = 0; i < this.otpLength; i++) {
        this.recoveryEmptyFieldsAfterBlur[i] = this.recoveryOtpDigits[i] === '';
      }
    }
  }

  onRecoveryInputFocus(event: any, index: number) {
    // Clear error state for this specific input when focused
    this.recoveryEmptyFieldsAfterBlur[index] = false;
    this.recoveryHadFocus = true;
  }

  // Phone OTP blur and focus handlers
  onPhoneInputBlur(event: any, index: number) {
    this.phoneHadFocus = true;
    
    // If the field is empty after blur, mark it for error display
    if (!event.target.value) {
      this.phoneEmptyFieldsAfterBlur[index] = true;
    }
    
    // Check if all fields are empty to show the error message
    const allEmpty = this.phoneOtpDigits.every(digit => digit === '');
    
    // Show error if all fields are empty after having focus
    this.showPhoneOtpError = allEmpty;
    
    // Update UI to mark which fields are empty
    this.updatePhoneEmptyFieldsState();
  }
  
  // Helper to update empty fields state
  updatePhoneEmptyFieldsState() {
    // Mark all empty fields with error state if we've had focus
    if (this.phoneHadFocus) {
      for (let i = 0; i < this.otpLength; i++) {
        this.phoneEmptyFieldsAfterBlur[i] = this.phoneOtpDigits[i] === '';
      }
    }
  }

  onPhoneInputFocus(event: any, index: number) {
    // Clear error state for this specific input when focused
    this.phoneEmptyFieldsAfterBlur[index] = false;
    this.phoneHadFocus = true;
  }

  // Recovery Email Validation
  // validateRecoveryOtp() {
  //   const enteredOtp = this.recoveryOtpDigits.join('');

  //       if (enteredOtp.length !== this.otpLength) {
  //     this.showErrorMessage = true;
  //     this.errorMessage = 'Please enter all 6 digits';
  //     return;
  //   }

  //   const otpVerificationPayload = {
  //   email: this.recoveryEmail, // Using recovery email for verification
  //   otp: enteredOtp,
  //   otpType: 'recovery-email' // Optional: to distinguish OTP type
  // };

  // this.RegistrationService.verifyRecoveryEmail(otpVerificationPayload).subscribe({
  //   next: (response) => {
  //     console.log('Recovery OTP verification response:', response);
      
  //   })
  //   }


    // if (enteredOtp === this.correctRecoveryOtp) {
    //   this.isEmailVerified = true;
    //   this.showErrorMessage = false;
    //   this.clearRecoveryTimer();
    //   this.enablePhoneVerification();
    // } else {
    //   this.recoveryAttempts++;
    //   this.showErrorMessage = true;

    //   if (this.recoveryAttempts >= this.maxAttempts + 1) {
    //     // Block after 5 wrong attempts, disable for 15 minutes
    //     this.isBlocked = true;
    //     this.errorMessage = 'Too many incorrect attempts.';
    //     this.clearRecoveryInputs();
    //     this.recoveryTimeRemaining = 15 * 60;
    //     this.updateRecoveryFormattedTime();
    //     this.clearRecoveryTimer();
    //     this.startRecoveryTimer();
    //   } else {
    //     // Show inline error
    //     this.errorMessage = 'Incorrect OTP. Please try again.';
    //     this.clearRecoveryInputs();
    //   }
    //   // Always show toast for wrong OTP (1st to 5th attempt)
    //   this.showOtpErrorToast(this.recoveryAttempts);
    // }


  // }

  // Update the validateRecoveryOtp method to use API call
// validateRecoveryOtp() {
//   const enteredOtp = this.recoveryOtpDigits.join('');

//   if (enteredOtp.length !== this.otpLength) {
//     this.showErrorMessage = true;
//     this.errorMessage = 'Please enter all 6 digits';
//     return;
//   }

//   // Prepare API request payload
//   const otpVerificationPayload = {
//     email: this.recoveryEmail, // Using recovery email for verification
//     otp: enteredOtp,
//   };

//   console.log('Verifying recovery email OTP via API:', otpVerificationPayload);

//   // Call the verify-email-otp API
//   this.RegistrationService.verifyRecoveryEmail(otpVerificationPayload).subscribe({
//     next: (response) => {
//       console.log('Recovery OTP verification response:', response);
      
//       if (response.success || response.status === 'success') {
//         // OTP is correct
//         this.isEmailVerified = true;
//         this.showErrorMessage = false;
//         this.clearRecoveryTimer();
//         this.enablePhoneVerification();
        
//         // Show success toast
//         this.toastTitle = '';
//         this.toastMessage = 'Recovery email verified successfully.';
//         this.toastType = 'success';
//         this.showToast = true;
//         this.setToastTimer();
//       } else {
//         // Handle API response indicating incorrect OTP
//         this.handleIncorrectRecoveryOtp();
//       }
//     },
//     error: (error) => {
//       console.error('Error verifying recovery OTP:', error);
      
//       // Handle different error scenarios
//       if (error.status === 400 || error.error?.message?.includes('incorrect') || error.error?.message?.includes('invalid')) {
//         // OTP is incorrect
//         this.handleIncorrectRecoveryOtp();
//       } else if (error.status === 429 || error.error?.message?.includes('attempts') || error.error?.message?.includes('blocked')) {
//         // Too many attempts - block user
//         this.handleTooManyAttempts();
//       } else {
//         // Generic error
//         this.showErrorMessage = true;
//         this.errorMessage = 'Unable to verify OTP. Please try again.';
//         this.showGenericErrorToast();
//       }
//     }
//   });
// }

validateRecoveryOtp() {
  const enteredOtp = this.recoveryOtpDigits.join('');
 
  if (enteredOtp.length !== this.otpLength) {
    this.showErrorMessage = true;
    this.errorMessage = 'Please enter all 6 digits';
    return of({ success: false });
  }
 
  // Prepare API request payload
  const otpVerificationPayload = {
    email: this.recoveryEmail, // Using recovery email for verification
    otp: enteredOtp,
  };
 
  console.log('Verifying recovery email OTP via API:', otpVerificationPayload);
 
  // Call the verify-email-otp API
  this.RegistrationService.verifyRecoveryEmail(otpVerificationPayload).subscribe({
    next: (response) => {
      console.log('Recovery OTP verification response:', response);
     
      if (
        response.status === 'success' &&
        response.data &&
        response.data.success === false &&
        response.data.message === 'Invalid OTP. Please try again.'
      ) {
        // Handle API response indicating incorrect OTP
        this.handleIncorrectRecoveryOtp();
        this.showOtpErrorToast(this.recoveryAttempts);
      } else if (response.data && response.data.success) {
        // OTP is correct
        this.isEmailVerified = true;
        this.setRecoveryEmailVerifiedFlag();
        // this.showErrorMessage = false;
        this.showErrorMessage = true;
        this.clearRecoveryTimer();
        this.enablePhoneVerification();
        // Show success toast
        this.toastTitle = '';
        this.toastMessage = 'Recovery email verified successfully.';
        this.toastType = 'success';
        this.showToast = true;
        this.setShortToastTimer();
      } else {
        // Fallback for any other error
        this.handleIncorrectRecoveryOtp();
      }
    },
    error: (error) => {
      console.error('Error verifying recovery OTP:', error);
     
      // Handle different error scenarios
      if (error.status === 400 || error.error?.message?.includes('incorrect') || error.error?.message?.includes('invalid')) {
        // OTP is incorrect
        this.handleIncorrectRecoveryOtp();
      } else if (error.status === 429 || error.error?.message?.includes('attempts') || error.error?.message?.includes('blocked')) {
        // Too many attempts - block user
        this.handleTooManyAttempts();
      } else {
        // Generic error
        this.showErrorMessage = true;
        this.errorMessage = 'Unable to verify OTP. Please try again.';
        this.showGenericErrorToast();
      }
    }
  });
}

// Helper method to handle incorrect OTP
private handleIncorrectRecoveryOtp() {
  this.recoveryAttempts++;
  this.showErrorMessage = true;

  if (this.recoveryAttempts >= this.maxAttempts) {
    // Block after max wrong attempts, disable for configured duration
    this.isBlocked = true;
    this.errorMessage = 'Too many incorrect attempts.';
    this.clearRecoveryInputs();
    this.recoveryTimeRemaining = this.otpLockoutDuration * 60;
    this.updateRecoveryFormattedTime();
    this.clearRecoveryTimer();
    this.startRecoveryTimer();
  } else {
    // Show inline error
    this.errorMessage = 'Incorrect OTP. Please try again.';
    this.clearRecoveryInputs();
  }
  
  // Always show toast for wrong OTP (1st to 5th attempt)
  this.showOtpErrorToast(this.recoveryAttempts);
}

// Helper method to handle too many attempts from API
private handleTooManyAttempts() {
  this.isBlocked = true;
  this.showErrorMessage = true;
  this.errorMessage = 'Too many incorrect attempts.';
  this.clearRecoveryInputs();
  this.recoveryTimeRemaining = this.otpLockoutDuration * 60;
  this.updateRecoveryFormattedTime();
  this.clearRecoveryTimer();
  this.startRecoveryTimer();
  
  // Show blocked toast
  this.toastTitle = '';
  this.toastMessage = `For your security, verification has been temporarily locked due to multiple incorrect code entries. You can try again in ${this.otpLockoutDuration} minutes.`;
  this.toastType = 'error';
  this.showToast = true;
  this.setToastTimer();
}

// Helper method for generic error toast
private showGenericErrorToast() {
  this.toastTitle = '';
  this.toastMessage = 'Unable to verify OTP. Please check your connection and try again.';
  this.toastType = 'error';
  this.showToast = true;
  this.setShortToastTimer();
}

  // Phone Number Validation
  validatePhoneOtp() {
    const enteredOtp = this.phoneOtpDigits.join('');

    if (enteredOtp.length !== this.otpLength) {
      this.showErrorMessage = true;
      this.errorMessage = 'Please enter all 6 digits';
      return;
    }
    if (enteredOtp === this.correctPhoneOtp) {
      this.isPhoneVerified = true;
      this.showErrorMessage = false;
      this.clearPhoneTimer();
      
      // Navigate to create-password after phone verification
      this.navigateToCreatePassword();
    } else {
      this.phoneAttempts++;
      this.showErrorMessage = true;

      if (this.phoneAttempts >= this.maxAttempts) {
        // Block after max wrong attempts, disable for configured duration
        this.isPhoneBlocked = true;
        this.errorMessage = 'Too many incorrect attempts.';
        this.clearPhoneInputs();
        this.phoneTimeRemaining = this.otpLockoutDuration * 60;
        this.updatePhoneFormattedTime();
        this.clearPhoneTimer();
        this.startPhoneTimer();
      } else {
        // Show inline error
        this.errorMessage = 'Incorrect OTP. Please try again.';
        this.clearPhoneInputs();
      }
      // Always show toast for wrong OTP (1st to 5th attempt)
      this.showOtpErrorToast(this.phoneAttempts);
    }
  }

  enablePhoneVerification() {
    // Send phone OTP automatically when recovery email is verified
    this.phoneOtpSent = true;
    console.log('Phone verification enabled - OTP sent to phone');
  }

  // Timer methods for Recovery Email
  startRecoveryTimer() {
    if (this.recoveryTimerInterval) {
      clearInterval(this.recoveryTimerInterval);
    }

    this.recoveryTimerInterval = setInterval(() => {
      if (this.recoveryTimeRemaining > 0) {
        this.recoveryTimeRemaining--;
        this.updateRecoveryFormattedTime();
        this.cdr.markForCheck();
        // Persist timer state every tick
        localStorage.setItem(`recoveryTimerState_${this.recoveryEmail}`, JSON.stringify({
          timeRemaining: this.recoveryTimeRemaining,
          lockoutUntil: this.recoveryResendDisabledUntil
        }));
      } else {
        clearInterval(this.recoveryTimerInterval);
        this.recoveryTimerInterval = null;
        localStorage.removeItem(`recoveryTimerState_${this.recoveryEmail}`);
        if (this.isBlocked) {
          // Reset attempts and block state after lock expires
          this.isBlocked = false;
          this.recoveryAttempts = 0;
          this.recoveryResendCount = 0;
          this.recoveryResendDisabledUntil = null;
          localStorage.removeItem('recoveryResendDisabledUntil');
          this.showErrorMessage = false;
          this.errorMessage = '';
        }
      }
    }, 1000);
  }

  clearRecoveryTimer() {
    if (this.recoveryTimerInterval) {
      clearInterval(this.recoveryTimerInterval);
      this.recoveryTimerInterval = null;
    }
  }

  updateRecoveryFormattedTime() {
    const minutes = Math.floor(this.recoveryTimeRemaining / 60);
    const seconds = this.recoveryTimeRemaining % 60;
    this.recoveryFormattedTime = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  clearRecoveryInputs() {
    for (let i = 0; i < this.otpLength; i++) {
      const input = document.getElementById(`recovery-otp-input-${i}`) as HTMLInputElement;
      if (input) {
        input.value = '';
      }
    }
    this.recoveryOtpDigits = new Array(this.otpLength).fill('');
    this.recoveryEmptyFieldsAfterBlur = new Array(this.otpLength).fill(false);
    this.showRecoveryOtpError = false;
  }

  // Timer methods for Phone
  startPhoneTimer() {
    this.phoneTimerInterval = setInterval(() => {
  if (this.phoneTimeRemaining > 0) {
    this.phoneTimeRemaining--;
    this.updatePhoneFormattedTime();
    this.cdr.markForCheck();
    // Persist timer state every tick
    this.sessionStorage.setItem(`phoneTimerState_${this.phone}`, JSON.stringify({
      timeRemaining: this.phoneTimeRemaining,
      lockoutUntil: this.phoneResendDisabledUntil,
      resendCount: this.phoneResendCount
    }));
  } else {
    clearInterval(this.phoneTimerInterval);
    this.phoneTimerInterval = null;
    // Timer expired, clear state
    this.sessionStorage.removeItem('phoneTimerState');
    this.phoneResendDisabledUntil = null;
    
    
    if (this.isPhoneBlocked) {
      this.phoneAttempts = 0;
      this.phoneResendCount = 0;
    }
    this.isPhoneBlocked = false;
  }
}, 1000);
  }

  clearPhoneTimer() {
    if (this.phoneTimerInterval) {
      clearInterval(this.phoneTimerInterval);
      this.phoneTimerInterval = null;
    }
  }

  updatePhoneFormattedTime() {
    const minutes = Math.floor(this.phoneTimeRemaining / 60);
    const seconds = this.phoneTimeRemaining % 60;
    this.phoneFormattedTime = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  clearPhoneInputs() {
    for (let i = 0; i < this.otpLength; i++) {
      const input = document.getElementById(`phone-otp-input-${i}`) as HTMLInputElement;
      if (input) {
        input.value = '';
      }
    }
    this.phoneOtpDigits = new Array(this.otpLength).fill('');
    this.phoneEmptyFieldsAfterBlur = new Array(this.otpLength).fill(false);
    this.showPhoneOtpError = false;
    this.phoneOtpDigits = new Array(this.otpLength).fill('');
  }

  // Navigation method
  navigateToCreatePassword() {
    console.log('Navigating to create-password page');
    const cleanedPhoneNumber = this.phoneNumber ? this.phoneNumber.replace(/\D/g, '') : '';
    const queryParams: any = {};
    if (this.email) {
      queryParams.email = this.email;
    }
    queryParams.phone = cleanedPhoneNumber;
    this.router.navigate(['/reg-form/create-password'], {
      queryParams: queryParams
    });
  }

  // New toast message methods for each specific case
  showOtpErrorToast(attempts: number) {
    this.toastTitle = '';
    
    const remainingAttempts = this.maxAttempts - attempts;
    
    if (attempts >= this.maxAttempts) {
      // Lockout message
      this.toastMessage = `For your security, verification has been temporarily locked due to multiple incorrect code entries. You can try again in ${this.otpLockoutDuration} minutes.`;
    } else if (remainingAttempts > 0) {
      // Show remaining attempts dynamically
      this.toastMessage = `The code entered is incorrect. You have ${remainingAttempts} attempt(s) remaining before verification is temporarily locked.`;
    } else {
      this.toastMessage = this.toastDbMessage['T0005'];
    }
    this.toastType = 'error';
    this.showToast = true;
    this.setToastTimer();
  }

  showResendLimitToast() {
    this.toastTitle = 'Resend Code should be possible only five times.';
    this.toastMessage = '';
    this.toastType = 'error';
    this.showToast = true;
    this.setToastTimer();
  }

  showContactSupportToast() {
    this.toastTitle = 'Your information has been sent to our support team';
    this.toastMessage = `A confirmation email has been sent to ${this.email}`;
    this.toastType = 'success';
    this.showToast = true;
    this.setToastTimer();
  }

  setToastTimer() {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
    this.toastTimer = setTimeout(() => {
      this.showToast = false;
    }, 7000); // 7 seconds display time
  }

  setShortToastTimer() {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
    this.toastTimer = setTimeout(() => {
      this.showToast = false;
    }, 3000); // 3 seconds display time
  }

  closeToast() {
    this.showToast = false;
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
  }

  // Recovery Email Resend Methods
  onResendRecoveryCode() {
  // Check if resend is disabled due to failed attempts
  const lockoutUntil = Number(localStorage.getItem(`recoveryResendDisabledUntil_${this.recoveryEmail}`) || '0');
  if (lockoutUntil && Date.now() < lockoutUntil) {
    this.recoveryResendDisabledUntil = lockoutUntil;
    this.recoveryTimeRemaining = Math.ceil((lockoutUntil - Date.now()) / 1000);
    this.updateRecoveryFormattedTime();
    this.startRecoveryTimer();
    this.isBlocked = true;
    return;
  }
  if (this.recoveryResendDisabledUntil && Date.now() < this.recoveryResendDisabledUntil) {
    return;
  }

  // For first 5 clicks (recoveryResendCount 0-4), send OTP, show 30s timer
  if (this.recoveryResendCount < this.maxResendCount) {
    const otpPayload = {
      email: this.recoveryEmail,
    };
    this.RegistrationService.sendRecoveryEmailOtp(otpPayload).subscribe({
      next: (response) => {
        if (response.success || response.status === 'success') {
          this.recoveryResendCount++;
          this.initializeRecoveryOtp();
          this.clearRecoveryInputs();
          this.isBlocked = false;
          this.showErrorMessage = false;
          this.errorMessage = '';
          this.recoveryTimeRemaining = this.recoveryResendTimer;
          this.updateRecoveryFormattedTime();
          this.startRecoveryTimer();
          // Show toast message T0050 on every successful resend
          const toastText = this.toastDbMessage['T0050'] || 'A new verification code has been sent to your recovery email.';
          this.toastTitle = '';
          this.toastMessage = toastText;
          this.toastType = 'success';
          this.showToast = true;
          this.setShortToastTimer();
        }
      }
    });
    return;
  }

  // On 6th click (recoveryResendCount === maxResendCount), block for configured duration, do NOT send OTP or increment count
  if (this.recoveryResendCount === this.maxResendCount) {
    this.isBlocked = true;
    const lockoutUntil = Date.now() + this.otpResendLockoutDuration * 60 * 1000;
    localStorage.setItem('recoveryResendDisabledUntil_${this.recoveryEmail}', lockoutUntil.toString());
    this.recoveryTimeRemaining = this.otpResendLockoutDuration * 60;
    this.updateRecoveryFormattedTime();
    this.startRecoveryTimer();
    return;
  }
}

  // Phone Resend Methods
  onResendPhoneCode() {
      const phoneTimerState = JSON.parse(this.sessionStorage.getItem('phoneTimerState_${this.phone}') || '{}');
      if (phoneTimerState && phoneTimerState.lockoutUntil && Date.now() < phoneTimerState.lockoutUntil) {
        this.phoneResendDisabledUntil = phoneTimerState.lockoutUntil;
        this.phoneTimeRemaining = Math.ceil((phoneTimerState.lockoutUntil - Date.now()) / 1000);
        this.updatePhoneFormattedTime();
        this.startPhoneTimer();
        this.isPhoneBlocked = true;
        // this.showErrorMessage = true;
        // this.errorMessage = 'Resend Code is disabled due to too many failed attempts. Please try again after 15 minutes.';
        return;
      }
      if (this.phoneResendCount === this.maxResendCount) {
    this.isPhoneBlocked = true;
    const lockoutUntil = Date.now() + this.otpResendLockoutDuration * 60 * 1000;
    this.phoneResendDisabledUntil = lockoutUntil;
    this.phoneTimeRemaining = this.otpResendLockoutDuration * 60;
    this.updatePhoneFormattedTime();
    this.startPhoneTimer();
    this.sessionStorage.setItem('phoneTimerState_${this.phone}', JSON.stringify({
      timeRemaining: this.phoneTimeRemaining,
      lockoutUntil: lockoutUntil,
      resendCount: this.phoneResendCount
    }));
    return;
  }
     
      this.phoneResendCount++;
      this.initializePhoneOtp();
      this.clearPhoneInputs();
      this.isPhoneBlocked = false;
      this.showErrorMessage = false;
      this.errorMessage = '';
      this.phoneTimeRemaining = this.phoneResendTimer;
      this.updatePhoneFormattedTime();
      this.startPhoneTimer();
      // Show toast message T0040 on every successful resend
      const toastText = this.toastDbMessage['T0050'] || 'A new verification code has been sent to your registered phone number.';
      this.toastTitle = '';
      this.toastMessage = toastText;
      this.toastType = 'success';
      this.showToast = true;
      this.setShortToastTimer();
      // Save timer state to sessionStorage
      this.sessionStorage.setItem('phoneTimerState', JSON.stringify({
        timeRemaining: this.phoneTimeRemaining,
        lockoutUntil: null,
        resendCount: this.phoneResendCount
      }));
  }

  getInputIds() {
    return Array(this.otpLength)
      .fill(0)
      .map((_, index) => index);
  }

  // onVerifyRecoveryEmail() {
  //   console.log('Recovery Email Verification');
  //   if (this.isBlocked) {
  //     // Show support confirmation message only when Contact Support is clicked
  //     this.showContactSupportToast();
  //   } else {
  //     // Otherwise validate OTP
  //     this.validateRecoveryOtp();
  //     if (!this.showErrorMessage) {
  //       // API call for recovery email verification
  //       let input = {
  //         otp: this.recoveryOtpDigits.join(''),
  //         email: this.email,
  //         recoveryEmail: this.recoveryEmail
  //       };
  //       this.RegistrationService.updateRecoveryEmail(input).subscribe({
  //         next: data => {
  //           console.log('Recovery email updated successfully:', data);
  //         },
  //         error: error => {
  //           console.error('Error updating recovery email:', error);
  //         }
  //       });

  //       // Show success toast
  //       this.toastTitle = '';
  //       this.toastMessage = 'Recovery email verified successfully.';
  //       this.toastType = 'success';
  //       this.showToast = true;
  //       this.setToastTimer();
  //     }
  //   }
  // }

  
onVerifyRecoveryEmail() {
    console.log('Recovery Email Verification');
    if (this.isBlocked) {
      // Show support confirmation message only when Contact Support is clicked
      this.showContactSupportToast();
      return;
    }
 
    this.validateRecoveryOtp().subscribe({
    next: (response) => {
      if (response.success === true ) {
        // OTP is correct, now update recovery email
        let input = {
          otp: this.recoveryOtpDigits.join(''),
          email: this.email,
          recoveryEmail: this.recoveryEmail
        };
        this.RegistrationService.updateRecoveryEmail(input).subscribe({
          next: data => {
            console.log('Recovery email updated successfully:', data);
          },
          error: error => {
            console.error('Error updating recovery email:', error);
          }
        });
                this.toastTitle = '';
        this.toastMessage = 'Recovery email verified successfully.';
        this.toastType = 'success';
        this.showToast = true;
        this.setShortToastTimer();
 
  // Update state
  this.isEmailVerified = true;
  this.setRecoveryEmailVerifiedFlag();
  this.showErrorMessage = false;
  this.clearRecoveryTimer();
  this.enablePhoneVerification();
      } else {
        // All error/toast handling is already done in validateRecoveryOtp
        // No need to do anything here
      }
    },
    error: (error) => {
      // All error/toast handling is already done in validateRecoveryOtp
    }
  });
  }


  onVerifyPhoneNumber() {
    console.log('Phone Number Verification');
    if (this.isPhoneBlocked) {
      // Show support confirmation message only when Contact Support is clicked
      this.showContactSupportToast();
    } else {
      // Otherwise validate OTP
      this.validatePhoneOtp();
      if (!this.showErrorMessage) {
        // API call for phone verification
        const cleanedPhoneNumber = this.phoneNumber.replace(/\D/g, '').replace(/^1(\d{10})$/, '$1');
        let input = {
          otp: this.phoneOtpDigits.join(''),
          email: this.email,
          phone: cleanedPhoneNumber,
          is_recovery_email_verified: true
        };
        this.RegistrationService.updatePhone(input).subscribe({
          next: data => {
            if (data.success) {
              console.log('Phone number updated successfully:', data);
              // Show success toast and navigate to create-password
              this.toastTitle = '';
              this.toastMessage = 'Phone number verified successfully';
              this.toastType = 'success';
              this.showToast = true;
              this.setToastTimer();

              // Navigate after toast is shown (after 3 seconds)
              setTimeout(() => {
                this.navigateToCreatePassword();
              }, 3000);
            }
          },
          error: error => {
            console.error('Error updating phone:', error);
          }
        });
      }
    }
  }

  canRecoveryResend(): boolean {
    return this.otpSent && this.recoveryTimeRemaining === 0 && this.recoveryResendCount < this.maxResendCount;
  }

  canPhoneResend(): boolean {
    return this.phoneOtpSent && this.phoneTimeRemaining === 0 && this.phoneResendCount < this.maxResendCount;
  }

  navigateToUpdateRecoveryEmail(): void {
    this.router.navigate(['../phone-update-recovery-email'], {
      relativeTo: this.route,
      queryParams: {
        email: this.email,
        recoveryEmail: this.recoveryEmail,
        phone: this.phone // Preserve phone number as well
      }
    });
  }

  get params() {
    const cleanedPhoneNumber = this.phoneNumber.replace(/\D/g, '');
    return this.recoveryEmail === '' ? {email: this.email, phone: cleanedPhoneNumber, source: 'phone-recovery-email'}
                                     : { email: this.email, phone: cleanedPhoneNumber, recoveryEmail: this.recoveryEmail, source: 'phone-recovery-email', recoveryEmailVerified: this.recoveryEmailVerified ? 'true' : 'false'}
  }
  navigateToPhoneUpdate(): void {
    // Set persistent flag so user doesn't need to verify again
    localStorage.setItem('recoveryEmailVerified', 'true');
    this.router.navigate(['../phone-update'], { 
      relativeTo: this.route,
      queryParams: this.params
    });
  }

  continueWithoutRecoveryEmail(): void {
    // Show confirmation modal
    this.showConfirmModal = true;
  }

  // Modal action methods
  confirmContinueWithoutRecovery(): void {
    this.showConfirmModal = false;
    // Show toast message for removal
    this.toastTitle = '';
    this.toastMessage = 'Recovery email removed successfully.';
    this.toastType = 'success';
    this.showToast = true;
    this.setShortToastTimer();

    // Navigate after toast is shown (after 3 seconds)
    setTimeout(() => {
      const updatedPhoneNumber = this.phone ? this.phone.replace(/\D/g, '') : '';
      const queryParams: any = {};
      if (this.email) {
        queryParams.email = this.email;
      }
      // Always include phone in queryParams, even if empty
      queryParams.phone = encodeURIComponent(updatedPhoneNumber);
      this.router.navigate(['../phone-verification'], {
        relativeTo: this.route,
        queryParams: queryParams
      });
    }, 3000);
  }

  cancelContinueWithoutRecovery(): void {
    // Just close the modal, no navigation
    this.showConfirmModal = false;
  }

  isRecoveryOtpDisabled(i: number): boolean {
  return !this.otpSent || this.isEmailVerified;
}

isPhoneOtpDisabled(i: number): boolean {
  return  this.isPhoneVerified || !this.isEmailVerified;
}

isResendRecoveryDisabled(): boolean {
  return  this.isBlocked || this.recoveryTimeRemaining > 0;
}
isResendPhoneDisabled(): boolean {
  return this.isPhoneBlocked || this.phoneTimeRemaining > 0 ;
}
}
